/**
 * Editar metadata de una credencial (F3b). La contraseña se cambia con "Rotar".
 * Notas y TOTP están cifrados y no se cargan: se reemplazan o se quitan.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { errMsg, isVaultLockedError, withUnlockRetry } from '@/hooks/useVault';
import { VAULT_AREAS, VAULT_POLICY, type VaultSecretMeta } from '../../../../shared/types/vault';

const NONE = '__none__';

interface EditSecretDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meta: VaultSecretMeta;
  hasNotes: boolean;
  isAdmin: boolean;
  myAreas: string[];
  ensureUnlocked: () => Promise<boolean>;
}

export function EditSecretDialog({ open, onOpenChange, meta, hasNotes, isAdmin, myAreas, ensureUnlocked }: EditSecretDialogProps) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const [name, setName] = useState(meta.name);
  const [username, setUsername] = useState(meta.username);
  const [url, setUrl] = useState(meta.url ?? '');
  const [area, setArea] = useState(meta.area ?? NONE);
  const [clientId, setClientId] = useState(meta.clientId ?? '');
  const [tags, setTags] = useState((meta.tags ?? []).join(', '));
  const [notes, setNotes] = useState('');
  const [clearNotes, setClearNotes] = useState(false);
  const [totpSeed, setTotpSeed] = useState('');
  const [clearTotp, setClearTotp] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(meta.name); setUsername(meta.username); setUrl(meta.url ?? ''); setArea(meta.area ?? NONE);
    setClientId(meta.clientId ?? ''); setTags((meta.tags ?? []).join(', '));
    setNotes(''); setClearNotes(false); setTotpSeed(''); setClearTotp(false);
  }, [open, meta]);

  const areaOptions = new Set(isAdmin ? VAULT_AREAS.map((a) => a.id) : myAreas);
  if (meta.area) areaOptions.add(meta.area);

  const update = trpc.vault.updateSecret.useMutation({
    onSuccess: () => {
      utils.vault.getSecret.invalidate({ secretId: meta.secretId });
      utils.vault.listSecrets.invalidate();
      toast.success(t('vault.edit.done', 'Credencial actualizada'));
      onOpenChange(false);
    },
    onError: (e) => { if (!isVaultLockedError(e)) toast.error(errMsg(e)); },
  });

  // el backend exige el Vault desbloqueado también para editar (auditoría 28/09: mostraba VAULT_LOCKED crudo)
  const submit = () =>
    withUnlockRetry(ensureUnlocked, () => update.mutateAsync({
      secretId: meta.secretId,
      version: meta.version,
      name: name.trim(),
      username: username.trim(),
      url: url.trim() || null,
      area: area === NONE ? null : area,
      clientId: clientId.trim() || null,
      tags: tags.split(',').map((s) => s.trim()).filter(Boolean).slice(0, 10),
      notes: clearNotes ? null : notes.trim() ? notes.trim() : undefined,
      totpSeed: clearTotp ? null : totpSeed.trim() ? totpSeed.trim() : undefined,
    })).catch(() => undefined);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('vault.edit.title', 'Editar credencial')}</DialogTitle>
          <DialogDescription>{t('vault.edit.sub', 'La contraseña se cambia con "Rotar". Notas y TOTP están cifrados: se reemplazan o se quitan.')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="space-y-1">
            <Label htmlFor="e-name">{t('vault.form.name', 'Nombre')}</Label>
            <Input id="e-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="e-user">{t('vault.form.username', 'Usuario / email')}</Label>
            <Input id="e-user" value={username} onChange={(e) => setUsername(e.target.value)} maxLength={200} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>{t('vault.form.area', 'Área')}</Label>
              <Select value={area} onValueChange={setArea}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('vault.form.area_none', 'Sin área')}</SelectItem>
                  {[...areaOptions].map((id) => (
                    <SelectItem key={id} value={id}>{VAULT_AREAS.find((a) => a.id === id)?.label ?? id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="e-client">{t('vault.form.client', 'Cliente')}</Label>
              <Input id="e-client" value={clientId} onChange={(e) => setClientId(e.target.value)} maxLength={40} />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="e-url">URL</Label>
            <Input id="e-url" value={url} onChange={(e) => setUrl(e.target.value)} maxLength={500} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="e-tags">{t('vault.form.tags', 'Etiquetas (separadas por coma)')}</Label>
            <Input id="e-tags" value={tags} onChange={(e) => setTags(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="e-totp">{meta.hasTotp ? t('vault.edit.totp_replace', 'Reemplazar semilla TOTP') : t('vault.form.totp', 'Semilla TOTP (opcional)')}</Label>
            <Input id="e-totp" value={totpSeed} onChange={(e) => setTotpSeed(e.target.value)} disabled={clearTotp} className="font-mono" maxLength={400} />
            {meta.hasTotp && (
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <Checkbox checked={clearTotp} onCheckedChange={(v) => setClearTotp(v === true)} />
                {t('vault.edit.totp_clear', 'Quitar el TOTP')}
              </label>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="e-notes">{hasNotes ? t('vault.edit.notes_replace', 'Reemplazar notas') : t('vault.form.notes', 'Notas (cifradas)')}</Label>
            <Textarea id="e-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} disabled={clearNotes} maxLength={VAULT_POLICY.notesMaxChars} />
            {hasNotes && (
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <Checkbox checked={clearNotes} onCheckedChange={(v) => setClearNotes(v === true)} />
                {t('vault.edit.notes_clear', 'Borrar las notas')}
              </label>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.ui.cancel', 'Cancelar')}</Button>
          <Button onClick={submit} disabled={!name.trim() || !username.trim() || update.isPending} data-tour="edit-save">
            {update.isPending ? t('vault.form.saving', 'Guardando…') : t('vault.edit.cta', 'Guardar')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
